import type { LucideIcon } from 'lucide-react';
import {
  Banknote,
  HeartPulse,
  Mail,
  MoreHorizontal,
  User,
} from 'lucide-react';

import type { RedactionCategory } from '../types/redaction';

/**
 * How a category looks. The categories themselves, with their labels and
 * descriptions, come from the API; this only decorates them. A category added
 * on the backend falls back to the defaults until it is given an entry here.
 * The statutory withholding grounds are not listed here either: the API serves
 * those with their labels, so the legislation is described in one place.
 */
export const CATEGORY_ICONS: Record<RedactionCategory, LucideIcon> = {
  name: User,
  contact: Mail,
  financial: Banknote,
  health: HeartPulse,
  other: MoreHorizontal,
};

export const DEFAULT_CATEGORY_ICON: LucideIcon = MoreHorizontal;

export const CATEGORY_STYLES: Record<RedactionCategory, string> = {
  name: 'bg-blue-100 text-blue-800',
  contact: 'bg-indigo-100 text-indigo-800',
  financial: 'bg-amber-100 text-amber-800',
  health: 'bg-red-100 text-red-800',
  other: 'bg-gray-100 text-gray-700',
};

export const DEFAULT_CATEGORY_STYLE = 'bg-gray-100 text-gray-700';

export const SOURCE_LABELS: Record<string, string> = {
  pattern: 'Matched a known format',
  model: 'Suggested by analysis',
  reviewer: 'Added by you',
};

export const LOCATED_BY_LABELS: Record<string, string> = {
  text_layer: 'Found in the page text',
  ocr: 'Read from an image',
  both: 'Found in the page text and an image',
};

export const CONFIDENCE_STYLES: Record<string, string> = {
  high: 'text-emerald-700',
  medium: 'text-amber-700',
  low: 'text-gray-500',
};

/** Reasons the API gives for refusing to redact a record. */
export const REDACTION_BLOCKED_MESSAGES: Record<string, string> = {
  unsupported_format:
    'Only PDF records can be redacted. Content is removed from the page itself, which has no equivalent in other formats.',
  source_changed:
    'A new version of this record was stored while you were reviewing it, so your selections no longer point at the right content. Start again to review the current file.',
};
